import {
    isoAndUnixZodSchema,
    type IsoAndUnixTimestamp,
} from "@@/shared/date-time/iso-and-unix";
import {
    timeRangeZodSchema,
    type TimeRange,
} from "@@/shared/date-time/time-range";
import type { Timezone} from "@@/shared/date-time/timezone";
import { timezoneZodType } from "@@/shared/date-time/timezone";
import {
    payoutTargetZodSchema,
    type PayoutTarget,
} from "@@/shared/financials/payout-targets/payout-target";
import type { Price } from "@@/shared/prices/price";
import type {
    ProviderId} from "@@/shared/providers/provider-id";
import {
    providerIdZodSchema,
} from "@@/shared/providers/provider-id";
import {
    storageItemPdfIdZodSchema,
    type StorageItemPdfId,
} from "@@/shared/storage-items/storage-item-pdfs/storage-item-pdf-id";
import {
    translatableZodSchema,
    type Translatable,
} from "@@/shared/translations/translation";
import {
    systemUserZodSchema,
    type SystemUser,
} from "@@/shared/users/system-user";
import { userIdZodSchema, type UserId } from "@@/shared/users/user-id";
import type { Base64} from "@@/utilities/base64";
import { base64ZodString } from "@@/utilities/base64";
import type { Except } from "type-fest";
import { z } from "zod";
import type {
    VatBreakdown} from "../shared/vat-breakdown.factory";
import {
    vatBreakdownZodSchema,
} from "../shared/vat-breakdown.factory";
import {
    settlementNoteIdZodSchema,
    type SettlementNoteId,
} from "./settlement-note-id";
import type { CustomFee } from "./settlements/custom-fee";
import type {
    HardCodedFee} from "./settlements/hard-coded-fees";
import {
    hardCodedFeeZodSchema,
} from "./settlements/hard-coded-fees";
import type { Settlement } from "./settlements/settlement";
import { settlementZodSchema } from "./settlements/settlement";

type SettlementNote = {
    readonly _id: SettlementNoteId;
    readonly _type: "SETTLEMENT_NOTE_BETA";
    readonly _creatorId: UserId | SystemUser;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly timeRange: TimeRange;
    readonly timezone: Timezone;
    readonly title: Translatable;
    readonly description?: Translatable;
    readonly ownerId: ProviderId;
    readonly status: "DRAFT" | "REMOVED" | "SETTLED" | "CANCELLED";
    // readonly items: SettlementNoteReportItem[];
    readonly items: Settlement[];
    readonly fees: (HardCodedFee | CustomFee)[];
    readonly vatBreakdown: VatBreakdown;
    // ? Kanske plocka bort denna helt, alternativt
    // ? göra den till ett objekt som innehåller flera uträknade värden?
    readonly orderAndOrderChangeCount: number;

    readonly payoutTotalPrice?: Price;

    /**
     * Payout amount in öre
     * @type {number}
     */
    readonly payoutAmount: number;
    readonly settlementDate?: IsoAndUnixTimestamp;
    readonly year: number;
    /**
     * Unique index of the settlement note \
     * Only unique within a specific year
     * @type {number}
     */
    readonly serialIndex?: number; // Unique
    /**
     * Format like: AN-{YEAR}-{serialNumber} \
     * Example: AN-2022-0001
     * @type {string}
     */
    readonly serialNumber?: string; // Should be AN-{YEAR}-{serialNumber}
    readonly storageItemId?: StorageItemPdfId;

    readonly storageItemDetailsId?: StorageItemPdfId;
    readonly storageItemDetailsExtendedId?: StorageItemPdfId;

    readonly payoutTarget: PayoutTarget;
};
const settlementNoteSerialNumberZodSchema = z.string();
// .refine(
//     value => {
//         const parts = value.split("-");
//         if (parts.length !== 3) return false;
//         if (parts[0] !== "AN") return false;
//         if (isNaN(Number.parseInt(parts[1]))) return false;
//         if (isNaN(Number.parseInt(parts[2]))) return false;
//         if (parts[1].length !== 4) return false;
//         return true;
//     },
//     { message: "Invalid serial number format" },
// );
const settlementNoteZodSchema: z.ZodSchema<SettlementNote> = z.object({
    _id: settlementNoteIdZodSchema,
    _type: z.literal("SETTLEMENT_NOTE_BETA"),
    _creatorId: z.union([userIdZodSchema, systemUserZodSchema]),
    _createdAt: isoAndUnixZodSchema,
    timeRange: timeRangeZodSchema,
    timezone: timezoneZodType,
    title: translatableZodSchema,
    description: translatableZodSchema.optional(),
    ownerId: providerIdZodSchema,
    status: z.enum(["DRAFT", "SETTLED", "CANCELLED"]),
    items: z.array(settlementZodSchema),
    fees: z.array(hardCodedFeeZodSchema),
    payoutAmount: z.number().min(0),
    orderAndOrderChangeCount: z.number(),
    vatBreakdown: vatBreakdownZodSchema,
    settlementDate: isoAndUnixZodSchema,
    year: z.number(),
    serialIndex: z.number().optional(),
    serialNumber: settlementNoteSerialNumberZodSchema.optional(),
    storageItemId: storageItemPdfIdZodSchema.optional(),
    payoutTarget: payoutTargetZodSchema,
});

type SettlementNoteCompressed = Except<SettlementNote, "_type" | "items"> & {
    _type: "SETTLEMENT_NOTE_COMPRESSED_BETA";
    data: Base64;
};
const settlementNodeZippedZodSchema: z.ZodSchema<SettlementNoteCompressed> =
    z.object({
        _id: settlementNoteIdZodSchema,
        _type: z.literal("SETTLEMENT_NOTE_COMPRESSED_BETA"),
        _creatorId: z.union([userIdZodSchema, systemUserZodSchema]),
        _createdAt: isoAndUnixZodSchema,
        timeRange: timeRangeZodSchema,
        timezone: timezoneZodType,
        title: translatableZodSchema,
        description: translatableZodSchema.optional(),
        ownerId: providerIdZodSchema,
        status: z.enum(["DRAFT", "SETTLED", "CANCELLED"]),
        data: base64ZodString,
        fees: z.array(hardCodedFeeZodSchema),
        payoutAmount: z.number().min(0),
        orderAndOrderChangeCount: z.number(),
        vatBreakdown: vatBreakdownZodSchema,
        settlementDate: isoAndUnixZodSchema,
        year: z.number(),
        serialIndex: z.number().optional(),
        serialNumber: settlementNoteSerialNumberZodSchema.optional(),

        storageItemId: storageItemPdfIdZodSchema.optional(),
        storageItemDetailsId: storageItemPdfIdZodSchema.optional(),
        storageItemDetailsExtendedId: storageItemPdfIdZodSchema.optional(),

        payoutTarget: payoutTargetZodSchema,
    });

export {
    settlementNodeZippedZodSchema,
    settlementNoteSerialNumberZodSchema,
    settlementNoteZodSchema,
};
export type { SettlementNote, SettlementNoteCompressed };
